import { useEffect, useRef, useState } from 'react'
import { getCasinoGameImageUrl, getCasinoGamePlaceholderImageUrl, hasCasinoGameImage } from '@/lib/casino'
import { CASINO_GAME_PLACEHOLDER_IMAGE } from '@/config/casino'
import { cn } from '@/lib/utils'
import type { CasinoGame } from '@/types/casino'

interface CasinoGameCardImageProps {
	game: CasinoGame
}

export function CasinoGameCardImage({ game }: CasinoGameCardImageProps) {
	const imageRef = useRef<HTMLImageElement>(null)
	const [isLoaded, setIsLoaded] = useState(false)
	const imageUrl = getCasinoGameImageUrl(game)
	const placeholderUrl = getCasinoGamePlaceholderImageUrl(game)
	const hasDistinctPlaceholder = Boolean(placeholderUrl) && placeholderUrl !== imageUrl

	useEffect(() => {
		setIsLoaded(false)

		if (imageRef.current?.complete && imageRef.current.naturalWidth > 0) {
			setIsLoaded(true)
		}
	}, [imageUrl])

	if (!hasCasinoGameImage(game)) {
		return <img src={CASINO_GAME_PLACEHOLDER_IMAGE} alt={game.casino_name} className='h-[200px] w-full rounded-2xl object-cover' />
	}

	if (!imageUrl) {
		return <div className='h-[200px] w-full rounded-2xl bg-secondary' />
	}

	return (
		<div className='relative h-[200px] w-full overflow-hidden rounded-2xl bg-secondary'>
			{hasDistinctPlaceholder ? <img src={placeholderUrl} alt='' aria-hidden className='absolute inset-0 h-full w-full scale-110 object-cover blur-xl' /> : null}
			<img
				ref={imageRef}
				src={imageUrl}
				alt={game.casino_name}
				onLoad={() => setIsLoaded(true)}
				className={cn(
					'absolute inset-0 h-full w-full object-cover transition-[opacity,filter,transform] duration-500 ease-out',
					isLoaded ? 'scale-100 opacity-100 blur-0' : hasDistinctPlaceholder ? 'opacity-0' : 'scale-110 opacity-100 blur-xl',
				)}
			/>
		</div>
	)
}
